import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import type React from 'react';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'app/modules/react-redux';
import ErrorMessageView from 'app/components/ErrorMessageView';
import * as Actions from 'app/store/actions';
import { getCatalog, getIsSupportedRegion, getProfile, getRegion } from 'app/store/reducers';
import NotAvailablePage from 'app/main/not-available/NotAvailablePage';
import { Redirect, useLocation } from 'react-router';

type Status = 'loading' | 'success' | 'error';
type Props = {
	children: React.ReactNode;
};

// FIXME::WARNING::this component cannot contain `useTranslation`. For some reason React.Suspense is not stopping this from rendering.
const InitializeApp = ({ children }: Props) => {
	const dispatch = useDispatch();
	const isSupportedRegion = useSelector(getIsSupportedRegion);
	const profile = useSelector(getProfile) as ReturnType<typeof getProfile> | undefined; // HACK::use actual type here - assume never undefined within the wrapped app;
	const region = useSelector(getRegion) as ReturnType<typeof getRegion> | undefined; // HACK::use actual type here - assume never undefined within the wrapped app;
	const catalog = useSelector(getCatalog) as ReturnType<typeof getCatalog> | undefined; // HACK::use actual type here - assume never undefined within the wrapped app;
	const dataFetched = profile && region && catalog && profile.tokens.expirationDate < Date.now();
	const [status, setStatus] = useState<Status>('loading');
	const location = useLocation();

	console.log('InitializeApp: component mounted/updated', {
		currentUrl: window.location.href,
		pathname: location.pathname,
		search: location.search,
		hasProfile: !!profile,
		hasRegion: !!region,
		hasCatalog: !!catalog,
		isSupportedRegion,
		dataFetched,
		status,
		timestamp: new Date().toISOString(),
	});

	useEffect(() => {
		if (location.pathname.includes('/login') || location.pathname.includes('/mfp-login')) {
			console.log('InitializeApp: skipping data fetch on login page');
			setStatus('success');
			return;
		}

		console.log('InitializeApp: starting data fetch');
		Promise.all([
			dispatch(Actions.getProfile()),
			// dispatch(Actions.getAdminedLicenseGroups()),
			dispatch(Actions.getRegion()).then(() => dispatch(Actions.getCatalog())),
		])
			.then(() => {
				console.log('InitializeApp: data fetch successful');
				setStatus('success');
			})
			.catch((error) => {
				console.error('InitializeApp: data fetch failed', {
					error: error instanceof Error ? error.message : String(error),
					currentUrl: window.location.href,
					timestamp: new Date().toISOString(),
				});
				setStatus('error');
			});
	}, [dispatch, location.pathname]);

	// HACK-ish::use session storage for SSO redirect
	const redirectPath = sessionStorage.getItem('redirect');

	console.log('InitializeApp: redirect handling', {
		redirectPath,
		currentPathname: location.pathname,
		isLoginPage: location.pathname.includes('/login'),
		shouldRedirect:
			redirectPath &&
			!location.pathname.includes('/error') &&
			!location.pathname.includes('/login') &&
			redirectPath !== location.pathname,
	});

	if (redirectPath) {
		sessionStorage.removeItem('redirect');
		if (
			!location.pathname.includes('/error') &&
			!location.pathname.includes('/login') &&
			redirectPath !== location.pathname
		) {
			console.log('InitializeApp: performing redirect', {
				from: location.pathname,
				to: redirectPath,
			});
			return <Redirect to={redirectPath} />;
		}
	}

	if (status === 'success' || dataFetched) {
		console.log('InitializeApp: rendering children', {
			status,
			dataFetched,
			hasProfile: !!profile,
			hasRegion: !!region,
			hasCatalog: !!catalog,
		});
		return <>{children}</>;
	}

	if (status === 'error') {
		console.log('InitializeApp: rendering error view');
		return <ErrorMessageView />;
	}

	if (!isSupportedRegion) {
		console.log('InitializeApp: rendering not available page');
		return <NotAvailablePage />;
	}

	console.log('InitializeApp: rendering splash screen');
	return <FuseSplashScreen />;
};

export default InitializeApp;
